import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Key,
  Search,
  Camera,
  FileCheck,
  Users,
  Phone,
  FileText,
  Wrench,
  Shield,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import StickyValuationCTA from "../components/StickyValuationCTA";
import api from "../lib/api";
import logger from "../lib/logger";

const ICONS = {
  Search,
  Camera,
  Users,
  FileCheck,
  Key,
  FileText,
  Wrench,
  Shield,
};

const DEFAULTS = {
  hero_eyebrow: "Our Services",
  title: "Lettings Service",
  description:
    "Professional letting services to find quality tenants quickly. From marketing to move-in, we handle every step with care and expertise.",
  hero_cta_text: "Book a Free Rental Valuation",
  process_section_title: "Our Lettings Process",
  process_section_subtitle: "A straightforward process designed to find you quality tenants quickly and efficiently.",
  process_items: [
    { step: "01", icon: "Search", title: "Free Valuation", description: "We visit your property to assess its condition and provide an accurate rental valuation based on current market demand." },
    { step: "02", icon: "Camera", title: "Professional Marketing", description: "High-quality photography, compelling descriptions, and listings on major property portals including Rightmove and Zoopla." },
    { step: "03", icon: "Users", title: "Tenant Viewings", description: "We conduct viewings at times convenient for you and provide feedback on each prospective tenant." },
    { step: "04", icon: "FileCheck", title: "Thorough Referencing", description: "Comprehensive checks including credit history, employment verification, previous landlord references, and Right to Rent." },
    { step: "05", icon: "Key", title: "Tenancy Setup", description: "We prepare all legal documentation, register the deposit, and handle the professional inventory and key handover." },
  ],
  features_section_title: "What's Included",
  features_items: [
    "Rightmove & Zoopla listings",
    "Professional photography",
    "Comprehensive tenant referencing",
    "Right to Rent compliance",
    "Deposit protection",
    "Tenancy agreement preparation",
    "Inventory service",
    "Key handover coordination",
  ],
  features_cta_text: "Get Started",
  image: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800",
  cta_title: "Ready to Let Your Property?",
  cta_description: "Get a free rental valuation and find out how quickly we can find you quality tenants.",
  cta_button_text: "Book a Free Rental Valuation",
  cta_phone: "01924 520163",
};

const LettingsPage = () => {
  const [content, setContent] = useState(DEFAULTS);

  useEffect(() => {
    let mounted = true;
    api
      .getContentSection("services_lettings")
      .then((res) => {
        if (mounted && res?.content) {
          setContent({ ...DEFAULTS, ...res.content });
        }
      })
      .catch((err) => {
        logger.warn("Failed to load services_lettings:", err?.message);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const renderIcon = (name, className) => {
    const Icon = ICONS[name] || Key;
    return <Icon className={className} />;
  };

  const ctaPhoneDigits = (content.cta_phone || "").replace(/\s/g, "");

  return (
    <div data-testid="lettings-page">
      <StickyValuationCTA source="lettings-page" ctaLabel="Free Rental Valuation" />
      {/* Hero */}
      <section className="bg-navy text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-gold font-medium text-sm tracking-wider uppercase mb-4 block">
              {content.hero_eyebrow}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mb-6">
              {content.title}
            </h1>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">{content.description}</p>
            <Button asChild size="lg" className="bg-gold hover:bg-gold-light text-navy font-semibold" data-testid="lettings-cta-valuation">
              <Link to="/contact">
                {content.hero_cta_text}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              {content.process_section_title}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">{content.process_section_subtitle}</p>
          </div>
          <div className="space-y-8" data-testid="lettings-process">
            {(content.process_items || []).map((item, index) => (
              <div key={index} className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-navy flex items-center justify-center">
                  <span className="font-heading font-bold text-gold">{item.step}</span>
                </div>
                <Card className="flex-1 border-slate-200">
                  <CardContent className="p-6 flex gap-6 items-start">
                    <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
                      {renderIcon(item.icon, "w-6 h-6 text-gold")}
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-semibold text-navy mb-2">{item.title}</h3>
                      <p className="text-slate-600">{item.description}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-28 bg-off-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img src={content.image} alt={content.title} className="rounded-lg shadow-2xl" />
            </div>
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-8">
                {content.features_section_title}
              </h2>
              <div className="space-y-4" data-testid="lettings-features">
                {(content.features_items || []).map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-gold flex-shrink-0" />
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Button asChild className="bg-navy hover:bg-navy-light text-white">
                  <Link to="/contact">
                    {content.features_cta_text}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6">{content.cta_title}</h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">{content.cta_description}</p>
          <Button asChild size="lg" className="bg-gold hover:bg-gold-light text-navy font-semibold">
            <Link to="/contact">
              {content.cta_button_text}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
          <div className="mt-6 flex items-center justify-center gap-2 text-white/70">
            <Phone className="w-5 h-5" />
            <span>Or call </span>
            <a href={`tel:${ctaPhoneDigits}`} className="text-gold hover:text-gold-light font-medium">
              {content.cta_phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LettingsPage;
